import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  X,
  Loader2,
  AlertCircle,
  FileText,
  MessageSquare,
  Download,
  FileSignature
} from 'lucide-react';
import api, { quotesAPI } from '../services/api';
import {
  PackageSelector,
  OptionalItemsTable,
  QuoteSummaryCard,
  LineItemsTable
} from '../components/quotes';
import SignatureCapture from '../components/common/SignatureCapture';
import { getSessionId } from '../utils/sessionTracking';
import { isPastDate } from '../utils/dates';
import { formatMoney } from '../utils/format';
import useAuthStore from '../context/authStore';
import { notifyError } from '../context/toastStore';

export default function LocalQuoteDetailPage() {
  // Approving or rejecting needs `quotes: accept` (Company Admin, Billing); the server refuses it otherwise.
  const canAccept = useAuthStore((s) => s.can)('quotes', 'accept');
  const { id } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [lineItems, setLineItems] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [customQuantities, setCustomQuantities] = useState({});
  const [customerNotes, setCustomerNotes] = useState('');

  // Action state
  const [saving, setSaving] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [signature, setSignature] = useState({ type: null, data: '', isValid: false });
  const [consented, setConsented] = useState(false);

  // Engagement tracking
  const viewIdRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const scrolledToBottomRef = useRef(false);

  // Handle signature changes
  const handleSignatureChange = useCallback((sig) => {
    setSignature(sig);
  }, []);

  // Track scroll to bottom
  useEffect(() => {
    const handleScroll = () => {
      if (scrolledToBottomRef.current) return;
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= docHeight - 100) {
        scrolledToBottomRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Send engagement data on unmount
  useEffect(() => {
    return () => {
      if (viewIdRef.current) {
        const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
        // Use sendBeacon for reliable delivery on page unload
        const data = JSON.stringify({
          timeOnPage,
          scrolledToBottom: scrolledToBottomRef.current
        });
        // A keepalive PATCH with the bearer token, not sendBeacon (POST only,
        // unauthenticated, and a hard-coded /api path): see InvoiceDetailPage.
        fetch(`${api.defaults.baseURL}/quotes/views/${viewIdRef.current}/engagement`, {
          method: 'PATCH',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: api.defaults.headers.common['Authorization'] || '' },
          body: data,
        }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      startTimeRef.current = Date.now(); // Reset start time on fetch

      const response = await quotesAPI.getLocal(id, {
        headers: { 'X-Session-Id': getSessionId() }
      });
      const data = response.data;

      // Capture view ID from response header for engagement tracking
      const viewId = response.headers?.['x-view-id'];
      if (viewId) {
        viewIdRef.current = viewId;
      }

      setQuote(data.quote);
      setLineItems(data.lineItems);
      setDocuments(data.documents || []);

      // Initialize selections from quote
      setSelectedPackage(data.quote.selectedPackage);
      setSelectedOptions(data.quote.selectedOptions || []);
      setCustomQuantities(data.quote.customQuantities || {});
      setCustomerNotes(data.quote.customerNotes || '');
    } catch (err) {
      console.error('Error fetching quote:', err);
      setError(err.response?.data?.error || 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals based on current selections
  const totals = useMemo(() => {
    if (!lineItems) return { subtotal: 0, taxAmount: 0, total: 0, packageTotal: 0, optionalTotal: 0 };

    let standardTotal = 0;
    let packageTotal = 0;
    let optionalTotal = 0;
    let taxAmount = 0;

    lineItems.all.forEach(item => {
      const qty = customQuantities[item.id] ?? item.quantity;
      const itemPrice = parseFloat(item.unit_price) * qty;
      const itemTax = item.taxable && item.tax_rate
        ? itemPrice * (parseFloat(item.tax_rate) / 100)
        : 0;

      // Package items
      if (item.package_group) {
        if (selectedPackage && item.package_option === selectedPackage) {
          packageTotal += itemPrice;
          taxAmount += itemTax;
        }
      }
      // Optional items (not in package)
      else if (item.is_optional) {
        if (selectedOptions.includes(item.id)) {
          optionalTotal += itemPrice;
          taxAmount += itemTax;
        }
      }
      // Standard items
      else {
        standardTotal += itemPrice;
        taxAmount += itemTax;
      }
    });

    const subtotal = standardTotal + packageTotal + optionalTotal;
    return {
      standardTotal,
      packageTotal,
      optionalTotal,
      subtotal,
      taxAmount,
      total: subtotal + taxAmount
    };
  }, [lineItems, selectedPackage, selectedOptions, customQuantities]);

  // Save selections
  const saveSelections = async () => {
    try {
      setSaving(true);
      await quotesAPI.updateSelections(id, {
        selectedPackage,
        selectedOptions,
        customQuantities,
        customerNotes
      });
    } catch (err) {
      notifyError('Your selections could not be saved.', err);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save selections when they change
  useEffect(() => {
    if (!loading && quote && quote.status !== 'accepted' && quote.status !== 'rejected') {
      const timer = setTimeout(saveSelections, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedPackage, selectedOptions, customQuantities]);

  const handleToggleOptional = (itemId) => {
    setSelectedOptions(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleUpdateQuantity = (itemId, quantity) => {
    setCustomQuantities(prev => ({ ...prev, [itemId]: quantity }));
  };

  const handleApprove = async () => {
    if (!signature.isValid || !consented) {
      return;
    }
    try {
      setActionLoading(true);
      await quotesAPI.approve(id, {
        notes: approveNotes,
        consent: true,
        signature: {
          type: signature.type,
          data: signature.data
        }
      });
      setShowApproveModal(false);
      setSignature({ type: null, data: '', isValid: false });
      fetchQuote(); // Refresh
    } catch (err) {
      console.error('Error approving quote:', err);
      setError(err.response?.data?.error || 'Failed to approve quote');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setActionLoading(true);
      await quotesAPI.reject(id, rejectReason);
      setShowRejectModal(false);
      fetchQuote(); // Refresh
    } catch (err) {
      console.error('Error rejecting quote:', err);
      setError(err.response?.data?.error || 'Failed to reject quote');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await quotesAPI.downloadPdf(id);

      // Track the download
      if (viewIdRef.current) {
        quotesAPI.trackPdfDownload(id, viewIdRef.current).catch(() => {});
      }

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create temp link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = quote.qwDocNumber
        ? `quote-${quote.qwDocNumber}.pdf`
        : `quote-${id.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => navigate('/quotes')}
          className="mt-4 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          Back to Quotes
        </button>
      </div>
    );
  }

  if (!quote) return null;

  const isEditable = !['accepted', 'rejected', 'expired'].includes(quote.status);
  const isExpired = quote.validUntil && isPastDate(quote.validUntil);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/quotes')}
          className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Quotes
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Standard Items */}
          {lineItems.standard.length > 0 && (
            <LineItemsTable items={lineItems.standard} title="Included Items" />
          )}

          {/* Package Groups */}
          {Object.entries(lineItems.packageGroups).map(([groupName, options]) => (
            <PackageSelector
              key={groupName}
              packageGroup={groupName}
              options={options}
              selectedOption={selectedPackage}
              onSelect={setSelectedPackage}
              disabled={!isEditable}
            />
          ))}

          {/* Optional Items */}
          {lineItems.optional.length > 0 && (
            <OptionalItemsTable
              items={lineItems.optional}
              selectedItems={selectedOptions}
              customQuantities={customQuantities}
              onToggleItem={handleToggleOptional}
              onUpdateQuantity={handleUpdateQuantity}
              disabled={!isEditable}
            />
          )}

          {/* Customer Notes */}
          {isEditable && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Your Notes</h3>
              </div>
              <textarea
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                onBlur={saveSelections}
                placeholder="Add any notes or questions about this quote..."
                rows={4}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {/* Terms & Conditions */}
          {quote.termsAndConditions && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Terms & Conditions</h3>
              </div>
              <div className="prose prose-sm max-w-none text-neutral-600 dark:text-neutral-300">
                <pre className="whitespace-pre-wrap font-sans text-sm">
                  {quote.termsAndConditions}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-6">
            {/* Quote Summary */}
            <QuoteSummaryCard
              quote={quote}
              totals={totals}
              standardItemsTotal={totals.standardTotal}
            />

            {/* Action Buttons */}
            {canAccept && isEditable && !isExpired && (
              <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
                <div className="space-y-3">
                  <button
                    onClick={() => setShowApproveModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    Approve Quote
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors"
                  >
                    <X className="w-5 h-5" />
                    Decline Quote
                  </button>
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium transition-colors disabled:opacity-50"
                  >
                    {downloadingPdf ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Download className="w-5 h-5" />
                    )}
                    Download PDF
                  </button>
                </div>

                {saving && (
                  <p className="text-center text-sm text-neutral-500 dark:text-neutral-400 mt-3">
                    Saving selections...
                  </p>
                )}
              </div>
            )}

            {/* Download PDF for non-editable quotes */}
            {!isEditable && (
              <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium transition-colors disabled:opacity-50"
                >
                  {downloadingPdf ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  Download PDF
                </button>
              </div>
            )}

            {/* Documents */}
            {documents.length > 0 && (
              <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Documents</h3>
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <a
                      key={doc.id}
                      href={doc.storage_path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
                    >
                      <FileText className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-neutral-900 dark:text-white truncate">
                          {doc.display_name || doc.filename}
                        </div>
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">
                          {(doc.file_size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approve Modal with E-Signature */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-lg w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                <FileSignature className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">Approve Quote</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Electronic signature required</p>
              </div>
            </div>

            <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg mb-6">
              <p className="text-green-800 dark:text-green-200">
                You are about to approve this quote for a total of{' '}
                <span className="font-semibold">
                  {formatMoney(totals.total)}
                </span>
              </p>
            </div>

            {/* E-Signature Capture */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                Your Signature <span className="text-red-500">*</span>
              </label>
              <SignatureCapture
                onChange={handleSignatureChange}
                signerName=""
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Notes (optional)
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Word for word what the server stores as consent (QUOTE_CONSENT.portal in
                services/contracts/signing.service.js); change both together. */}
            <label className="mb-6 flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300">
              <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>I am authorized to accept this quote on behalf of my organization. I agree to sign electronically, and my electronic signature has the same effect as my handwritten signature.</span>
            </label>

            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setSignature({ type: null, data: '', isValid: false });
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading || !signature.isValid || !consented}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Sign & Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Decline Quote</h3>
            <p className="text-neutral-600 dark:text-neutral-300 mb-4">
              Are you sure you want to decline this quote? This action cannot be undone.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Let us know why you're declining..."
                rows={3}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                {actionLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <X className="w-5 h-5" />
                    Decline
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
